import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CompanyProfile from './pages/CompanyProfile';
import Clients from './pages/Clients';
import Services from './pages/Services';
import AllInvoices from './pages/AllInvoices';
import CreateInvoice from './pages/CreateInvoice';
import InvoiceDetails from './pages/InvoiceDetails';
import AllReceipts from './pages/AllReceipts';
import ManualReceipt from './pages/ManualReceipt';
import ReceiptDetails from './pages/ReceiptDetails';
import RevisedInvoicesReceipts from './pages/RevisedInvoicesReceipts';
import EditableInvoiceDetails from './pages/EditableInvoiceDetails';
import EditableReceiptDetails from './pages/EditableReceiptDetails';
import PublicInvoice from './pages/PublicInvoice';
import PublicReceipt from './pages/PublicReceipt';
import Accounts from './pages/Accounts';
import Expenses from './pages/Expenses';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-3 border-indigo-200 border-t-indigo-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      {/* Public share pages — no auth, outside ProtectedRoute */}
      <Route path="/share/invoice/:token" element={<PublicInvoice />} />
      <Route path="/share/receipt/:token" element={<PublicReceipt />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/profile" element={<CompanyProfile />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/invoices" element={<AllInvoices />} />
                  <Route path="/invoice/create" element={<CreateInvoice />} />
                  <Route path="/invoice/:id" element={<InvoiceDetails />} />
                  <Route path="/receipts" element={<AllReceipts />} />
                  <Route path="/receipt/manual" element={<ManualReceipt />} />
                  <Route path="/receipt/:id" element={<ReceiptDetails />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/accounts" element={<Accounts />} />
                  <Route path="/revised" element={<RevisedInvoicesReceipts />} />
                  <Route path="/revised/invoice/:id" element={<EditableInvoiceDetails />} />
                  <Route path="/revised/receipt/:id" element={<EditableReceiptDetails />} />
                </Routes>
              </motion.div>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;